import React from 'react';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import './Signup.css';
import { SignupProvider, useSignup } from '../components/SignupFlow/SignupProvider';
import SignupStepRouter from '../components/SignupFlow/SignupStepRouter';

// Progress indicator component
const SignupProgress: React.FC = () => {
  const { state } = useSignup();
  const { currentStep } = state;

  const steps = [
    { key: 'restaurant', label: 'Restaurant Info', number: 1 },
    { key: 'manager', label: 'Manager Details', number: 2 },
    { key: 'tables', label: 'Service Pricing', number: 3 },
    { key: 'payment', label: 'Payment', number: 4 },
  ];

  const getCurrentStepNumber = () => {
    const step = steps.find(s => s.key === currentStep);
    return step ? step.number : 1;
  };

  const currentStepNumber = getCurrentStepNumber();

  return (
    <div className="signup-progress">
      <div className="progress-bar">
        {steps.map((step) => (
          <div 
            key={step.key}
            className={`progress-step ${currentStepNumber >= step.number ? 'active' : ''} ${currentStepNumber > step.number ? 'completed' : ''}`}
          >
            <div className="step-number">
              {currentStepNumber > step.number ? '✓' : step.number}
            </div>
            <div className="step-label">{step.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SignupContent: React.FC = () => {
  const { state } = useSignup();
  const showSidebar = ['restaurant', 'manager'].includes(state.currentStep);

  return (
    <div className={`signup-content ${showSidebar ? 'with-sidebar' : 'full-width'}`}>
      <div className="signup-form-container">
        <SignupStepRouter />
      </div>

      {showSidebar && (
        <div className="signup-sidebar">
          <div className="pricing-info">
            <h3>Simple Flat Pricing</h3>
            <div className="pricing-highlights">
              <div className="price-item">
                <div className="price-label">Complete Solution</div>
                <div className="price-value">$379 per month</div>
              </div>
            </div>
          </div>

          <div className="features-summary">
            <h3>What You Get</h3>
            <ul>
              <li>✅ Unlimited Staff Accounts</li>
              <li>✅ NFC TableChat Cards</li>
              <li>✅ Two Tablet Kiosks</li>
              <li>✅ Real-Time Messaging</li>
              <li>✅ Chat Moderation Reporting</li>
              <li>✅ Email Support</li>
            </ul>
          </div>

        </div>
      )}
    </div>
  );
};

const Signup: React.FC = () => {
  return (
    <SignupProvider>
      <SEO 
        title="Sign Up - TableChat | Get Started"
        description="Sign up for TableChat and transform your restaurant service with NFC-powered customer communication. $379/month for complete solution."
        canonical="https://tablechat.io/signup"
      />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Sign Up', url: 'https://tablechat.io/signup' },
      ])} />
      <div className="signup-page">
        <div className="container">
          <SignupHeader />
          <SignupProgress />
          <SignupContent />
        </div>
      </div>
    </SignupProvider>
  );
};

// Header component that only shows on the first page
const SignupHeader: React.FC = () => {
  const { state } = useSignup();
  
  // Only show header on the restaurant info page (first page)
  if (state.currentStep !== 'restaurant') {
    return null;
  }
  
  return (
      <div className="signup-header">
      <h1>Get Started with TableChat</h1>
      <p>Enhance your restaurant's customer service with TableChat technology.</p>
    </div>
  );
};

export default Signup;
